import React, { useState } from 'react';
import { styles } from './adminStyles';
import { EyeIcon, PencilIcon } from './navIcons';
import AdminBidDetails from './AdminBidDetails';
import AuctionManagement from './AuctionManagement';
import ZonesModal from './ZonesModal';
import { getAuctionTypeLabel } from '../../../config/auctionTypeConfig';
import { getDisplayStatus } from '../../../config/eventStatus';

const AdminOverview = ({
  processedEvents, overviewLoading, overviewData, zoneCoverage, sortConfig, handleSort,
  openQuickAuctionDetails, openClosure,
  modalLiveBids, modalZones, modalBidsLoading, loadBidsForAuction,
  handleSaveDecision, handleDeleteBid,
  handleWithdrawWinner, handlePromoteBid, fetchPromotionHeadroom,
  fetchBackfillPreview, handleBackfillWinners,
  libraryActions, loadEditFormForAuction, algorithms,
  currentEv, isClosed, isManual,
  editName, setEditName,
  editStartDate, setEditStartDate, editEndDate, setEditEndDate,
  editMaxPayout, setEditMaxPayout, editTerms, setEditTerms,
  editMinBenefitCostRatio, setEditMinBenefitCostRatio,
  editEventSummary, setEditEventSummary, editEventSummaryUrl, setEditEventSummaryUrl,
  editActions, setEditActions, toggleEditAction, updateEditActionBudget, updateEditActionGrantPrice,
  updateEditActionMaxPaymentPerFarm, updateEditActionMaxContributionPercent, updateEditActionMaxPaymentPerUnit,
  updateEditActionOptionGrantPayment,
  updateEditActionQuestion, updateEditActionFundingOptional,
  editOutcomeValues, handleEditWeightChange,
  editFundspreaderClearingMetric, setEditFundspreaderClearingMetric,
  editFundspreaderOutcomeToken, setEditFundspreaderOutcomeToken,
  hasChanges, setHasChanges, handleUpdateEventSettings, handleDiscardChanges,
  zones, globalZones, focusAreaLayers,
  handleApplyFocusAreaLayer,
  handleClearZones, handleEditZoneWeight, handleDeleteZone,
  zonesModalAuctionId, openZonesForAuction, closeZonesModal,
  handleArchiveEvent, archivingEventId
}) => {
  const [isBidModalOpen, setIsBidModalOpen] = useState(false);
  const [selectedAuctionId, setSelectedAuctionId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredEvents = statusFilter === 'all'
    ? processedEvents
    : processedEvents.filter(ev => getDisplayStatus(ev) === statusFilter);

  const openBidModal = (auctionId) => {
    setSelectedAuctionId(auctionId);
    setIsBidModalOpen(true);
    loadBidsForAuction(auctionId);
  };

  const closeBidModal = () => {
    setIsBidModalOpen(false);
    setSelectedAuctionId(null);
  };

  const modalAuction = processedEvents.find(ev => ev._id === selectedAuctionId);
  const zonesModalAuction = processedEvents.find(ev => ev._id === zonesModalAuctionId);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAuctionIdForEdit, setSelectedAuctionIdForEdit] = useState(null);

  const openEditModal = (auctionId) => {
    setSelectedAuctionIdForEdit(auctionId);
    setIsEditModalOpen(true);
    loadEditFormForAuction(auctionId);
  };

  // Guards against silently losing unsaved changes — closing (✕ button or
  // backdrop click) with hasChanges asks the admin to choose Save or Discard
  // rather than just closing.
  const closeEditModal = () => {
    if (hasChanges) {
      if (window.confirm("You have unsaved changes.\n\nClick OK to SAVE them, or Cancel to DISCARD them and close.")) {
        handleUpdateEventSettings();
      } else {
        handleDiscardChanges();
      }
    }
    setIsEditModalOpen(false);
    setSelectedAuctionIdForEdit(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

        <div style={{...styles.card, minHeight: '200px', marginBottom: 0}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div>
                    <h2 style={{margin:0, ...styles.h2}}>Live Events</h2>
                    <p style={{margin: '4px 0 0', fontSize: '0.8rem', color: '#475569', fontFamily: '"Inter", sans-serif'}}>
                        Click column header to sort. Use status filter to hide closed events →
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '0.85rem', color: '#475569', fontFamily: '"Inter", sans-serif' }}>Status</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{ ...styles.select, width: 'auto', marginBottom: 0, padding: '6px 10px', fontSize: '0.85rem' }}
                    >
                        <option value="all">All</option>
                        <option value="draft">Draft</option>
                        <option value="active">Active</option>
                        <option value="ended">Ended</option>
                        <option value="closed">Closed</option>
                    </select>
                </div>
            </div>
            {overviewLoading ? <p style={styles.textMuted}>Loading Event Data...</p> : (
                <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '60vh', border: '1px solid #eee', borderRadius: '4px' }}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.9rem'}}>
                    <thead>
                        <tr style={{backgroundColor:'#f0f2f5', borderBottom:'1px solid #ddd', textAlign:'left'}}>
                            <th onClick={() => handleSort('name')} style={{...styles.th, cursor:'pointer'}}>Event Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                            <th onClick={() => handleSort('status')} style={{...styles.th, cursor:'pointer', width:'110px'}}>Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                            <th onClick={() => handleSort('endDate')} style={{...styles.th, cursor:'pointer'}}>Closing Date {sortConfig.key === 'endDate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                            <th onClick={() => handleSort('auctionType')} style={{...styles.th, cursor:'pointer'}}>Event Type {sortConfig.key === 'auctionType' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                            <th style={styles.th}>Layers</th>
                            <th style={styles.th}>Budget</th>
                            <th style={styles.th}>No. Bids</th>
                            <th style={styles.th}>Total Value</th>
                            <th style={{...styles.th, textAlign: 'right'}}>Event Report</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredEvents.map(ev => {
                            const evStats = overviewData[ev._id] || {};

                            const displayStatus = getDisplayStatus(ev);
                            let badgeText = ev.status.toUpperCase();
                            let badgeColor = '#FFFFFF';
                            let badgeBg = '#0D9488';

                            if (displayStatus === 'active') {
                                badgeText = 'OPEN';
                                badgeColor = '#FFFFFF';
                                badgeBg = '#0D9488';
                            } else if (displayStatus === 'ended') {
                                badgeText = 'ENDED - AWAITING REVIEW';
                                badgeColor = '#FFFFFF';
                                badgeBg = '#F59E0B';
                            } else if (displayStatus === 'closed' && ev.closureType === 'manual_early') {
                                badgeText = 'CLOSED EARLY';
                                badgeColor = '#FFFFFF';
                                badgeBg = '#DC2626';
                            } else if (displayStatus === 'closed') {
                                badgeText = 'CLOSED';
                                badgeColor = '#F8FAFC';
                                badgeBg = '#0F172A';
                            }

                            const isClosedRow = ev.status === 'closed';
                            const displayDate = (isClosedRow && ev.updatedAt) ? new Date(ev.updatedAt) : new Date(ev.endDate);
                            const dateString = displayDate.toLocaleDateString('en-GB');

                            const auctionTypeDisplay = getAuctionTypeLabel(ev.auctionType);

                            const safeTotalBudget = (ev.auctionType === 'unified_vfm' || ev.auctionType === 'grants') ? (ev.totalBudget || 0) : (ev.allowedActions || []).reduce((sum, a) => sum + (a.budget || 0), 0);
                            const safeTotalCount = evStats.totalCount || 0;
                            const safeTotalVal = evStats.totalVal || 0;
                            const coverage = (zoneCoverage && zoneCoverage[ev._id]) || {};

                            const layerBadgeStyle = (active) => ({
                                padding: '3px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold',
                                backgroundColor: active ? '#0D9488' : '#E2E8F0', color: active ? '#FFFFFF' : '#94A3B8',
                                fontFamily: '"Inter", sans-serif', whiteSpace: 'nowrap'
                            });

                            return (
                                <tr key={ev._id} style={{borderBottom:'1px solid #eee', backgroundColor: '#FFFFFF', transition: 'background-color 0.2s'}}>
                                    <td style={{...styles.td, fontWeight:'bold', borderRight:'1px solid #eee'}}>{ev.name}</td>
                                    <td style={{...styles.td, borderRight:'1px solid #eee'}}>
                                        <span style={{padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', backgroundColor: badgeBg, color: badgeColor, display:'inline-block', minWidth:'70px', textAlign:'center', fontWeight: 'bold'}}>{badgeText}</span>
                                    </td>
                                    <td style={{...styles.td, borderRight:'1px solid #eee'}}>{dateString} {isClosedRow && <span style={{fontSize:'0.7rem', color:'#0D9488', display:'block'}}>(Actual)</span>}</td>
                                    <td style={{...styles.td, borderRight:'1px solid #eee', color: '#475569'}}>{auctionTypeDisplay}</td>
                                    <td style={{...styles.td, borderRight:'1px solid #eee'}}>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button
                                                type="button"
                                                onClick={() => openZonesForAuction(ev._id)}
                                                title="Open Event Focus Area (Zones)"
                                                style={{ ...layerBadgeStyle(coverage.hasFocusArea), border: 'none', cursor: 'pointer' }}
                                            >
                                                Focus Area
                                            </button>
                                        </div>
                                    </td>
                                    <td style={{...styles.td, fontWeight:'bold', color:'#0D9488'}}>£{safeTotalBudget.toLocaleString('en-GB')}</td>
                                    <td style={styles.td}>{safeTotalCount}</td>
                                    <td style={styles.td}>
                                        £{Math.round(safeTotalVal).toLocaleString('en-GB')}
                                    </td>

                                    <td style={{...styles.td, textAlign: 'right'}}>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                            <button
                                                onClick={() => openQuickAuctionDetails(ev._id)}
                                                style={{ border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', fontFamily: '"Inter", sans-serif', backgroundColor: '#0D9488', color: '#FFFFFF' }}
                                                title="Quick Event Details Report — the live Bid Details data plus a summary box"
                                            >
                                                Bids Report
                                            </button>
                                            <button
                                                onClick={() => openBidModal(ev._id)}
                                                style={{ ...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                                            >
                                                <EyeIcon size={14} /> View Bids
                                            </button>
                                            <button
                                                onClick={() => openEditModal(ev._id)}
                                                style={{ ...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                                            >
                                                <PencilIcon size={14} /> Edit Event Details
                                            </button>
                                            {isClosedRow ? (
                                                <button
                                                    onClick={() => handleArchiveEvent(ev._id)}
                                                    disabled={archivingEventId === ev._id}
                                                    style={{ ...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem', opacity: archivingEventId === ev._id ? 0.6 : 1, cursor: archivingEventId === ev._id ? 'not-allowed' : 'pointer' }}
                                                >
                                                    {archivingEventId === ev._id ? 'Archiving...' : 'Add to Archive?'}
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => openClosure(ev._id)}
                                                    style={{ ...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem' }}
                                                >
                                                    Test & Close
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {filteredEvents.length === 0 && <tr><td colSpan="9" style={{padding:'20px', textAlign:'center', color:'#475569'}}>No events found.</td></tr>}
                    </tbody>
                </table>
                </div>
            )}
        </div>

        {isBidModalOpen && selectedAuctionId && (
            <div style={styles.overlay} onClick={closeBidModal}>
                <div style={{...styles.modal, display: 'flex', flexDirection: 'column', overflow: 'hidden'}} onClick={(e) => e.stopPropagation()}>
                    <div style={{padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC', flexShrink: 0}}>
                        <h2 style={{margin: 0, ...styles.h2}}>Bid Details {modalAuction ? `— ${modalAuction.name}` : ''}</h2>
                        <button onClick={closeBidModal} style={{ ...styles.btnReversed, padding: '8px 16px' }}>Close ✕</button>
                    </div>
                    <div style={{flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: '#F8FAFC'}}>
                        <AdminBidDetails
                            auctionId={selectedAuctionId}
                            isModal
                            liveBids={modalLiveBids}
                            bidsLoading={modalBidsLoading}
                            zones={modalZones}
                            currentEv={modalAuction}
                            isClosed={modalAuction?.status === 'closed'}
                            isManual={modalAuction?.auctionType === 'manual'}
                            handleSaveDecision={handleSaveDecision}
                            handleDeleteBid={handleDeleteBid}
                            handleWithdrawWinner={handleWithdrawWinner}
                            handlePromoteBid={handlePromoteBid}
                            fetchPromotionHeadroom={fetchPromotionHeadroom}
                            fetchBackfillPreview={fetchBackfillPreview}
                            handleBackfillWinners={handleBackfillWinners}
                        />
                    </div>
                </div>
            </div>
        )}

        {isEditModalOpen && selectedAuctionIdForEdit && (
            <div style={styles.overlay} onClick={closeEditModal}>
                <div style={{...styles.modal, display: 'flex', flexDirection: 'column', overflow: 'hidden'}} onClick={(e) => e.stopPropagation()}>
                    <div style={{padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC', flexShrink: 0}}>
                        <h2 style={{margin: 0, ...styles.h2}}>Edit Event {currentEv ? `— ${editName || currentEv.name}` : ''}</h2>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={handleUpdateEventSettings}
                                disabled={!hasChanges}
                                style={{ ...styles.btnPrimary, padding: '8px 16px', opacity: hasChanges ? 1 : 0.5, cursor: hasChanges ? 'pointer' : 'not-allowed' }}
                            >
                                Save
                            </button>
                            <button onClick={closeEditModal} style={{ ...styles.btnReversed, padding: '8px 16px' }}>Close ✕</button>
                        </div>
                    </div>
                    <div style={{flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: '#F8FAFC'}}>
                        <AuctionManagement
                            mode="edit"
                            auctionId={selectedAuctionIdForEdit}
                            currentEv={currentEv} isClosed={isClosed} isManual={isManual}
                            libraryActions={libraryActions} algorithms={algorithms}
                            editName={editName} setEditName={setEditName}
                            editStartDate={editStartDate} setEditStartDate={setEditStartDate} editEndDate={editEndDate} setEditEndDate={setEditEndDate}
                            editMaxPayout={editMaxPayout} setEditMaxPayout={setEditMaxPayout} editTerms={editTerms} setEditTerms={setEditTerms}
                            editMinBenefitCostRatio={editMinBenefitCostRatio} setEditMinBenefitCostRatio={setEditMinBenefitCostRatio}
                            editEventSummary={editEventSummary} setEditEventSummary={setEditEventSummary} editEventSummaryUrl={editEventSummaryUrl} setEditEventSummaryUrl={setEditEventSummaryUrl}
                            editActions={editActions} setEditActions={setEditActions} toggleEditAction={toggleEditAction} updateEditActionBudget={updateEditActionBudget} updateEditActionGrantPrice={updateEditActionGrantPrice}
                            updateEditActionMaxPaymentPerFarm={updateEditActionMaxPaymentPerFarm} updateEditActionMaxContributionPercent={updateEditActionMaxContributionPercent}
                            updateEditActionMaxPaymentPerUnit={updateEditActionMaxPaymentPerUnit}
                            updateEditActionOptionGrantPayment={updateEditActionOptionGrantPayment}
                            updateEditActionQuestion={updateEditActionQuestion} updateEditActionFundingOptional={updateEditActionFundingOptional}
                            editOutcomeValues={editOutcomeValues} handleEditWeightChange={handleEditWeightChange}
                            editFundspreaderClearingMetric={editFundspreaderClearingMetric} setEditFundspreaderClearingMetric={setEditFundspreaderClearingMetric}
                            editFundspreaderOutcomeToken={editFundspreaderOutcomeToken} setEditFundspreaderOutcomeToken={setEditFundspreaderOutcomeToken}
                            hasChanges={hasChanges} setHasChanges={setHasChanges} handleUpdateEventSettings={handleUpdateEventSettings} handleDiscardChanges={handleDiscardChanges}
                        />
                    </div>
                </div>
            </div>
        )}

        {zonesModalAuctionId && (
            <ZonesModal
                auctionName={zonesModalAuction?.name}
                onClose={closeZonesModal}
                zones={zones}
                focusAreaLayers={focusAreaLayers}
                globalZones={globalZones}
                isClosed={zonesModalAuction?.status === 'closed'}
                onApplyExisting={handleApplyFocusAreaLayer}
                onClearAll={handleClearZones}
                onEditZone={handleEditZoneWeight}
                onDeleteZone={handleDeleteZone}
            />
        )}
    </div>
  );
};

export default AdminOverview;
